import * as d3 from 'd3'
import * as geometric from 'geometric'

import {
  ConnectionI,
  DeviceI,
  ImageOffsetI,
  Line,
  PlacedDeviceI,
  Point,
  RectI,
  Size,
} from '../types/types'
import {
  FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS,
  FLOOR_PLAN_CHECKPOINT_ACTION_OPACITY,
  FloorPlanVisualColors,
} from './floorPlanVisuals'

export const subtractFromBigger = (a1, a2) => (a1 > a2 ? a1 - a2 : a2 - a1)
export const subtractFromSmaller = (a1, a2) => (a1 < a2 ? a1 - a2 : a2 - a1)

export const translateAngleTo360Range = (angle) => {
  return angle < 0 ? angle + 360 : angle
}

export const translateAngleTo180Range = (angle) => {
  return angle > 180 ? angle - 360 : angle
}

export function getImageOffset(imageSize: Size, areaSize: Size): ImageOffsetI {
  const [imageWidth, imageHeight] = imageSize
  const [areaWidth, areaHeight] = areaSize
  let xOffset = 0
  let yOffset = 0
  if (imageWidth <= areaWidth) {
    xOffset = areaWidth / 2 - imageWidth / 2
  } else {
    xOffset = -((imageWidth - areaWidth) / 2)
  }

  if (imageHeight <= areaHeight) {
    yOffset = areaHeight / 2 - imageHeight / 2
  } else {
    yOffset = -((imageHeight - areaHeight) / 2)
  }

  return { x: xOffset, y: yOffset }
}

export function getPointProjectedOffset(
  originalOffsetX,
  originalOffsetY,
  imageOffsetX,
  imageOffsetY
): Point {
  const projectedOffsetX = originalOffsetX + imageOffsetX
  const projectedOffsetY = originalOffsetY + imageOffsetY

  return [projectedOffsetX, projectedOffsetY]
}

export function getPointOriginalOffsetX(pointX, imageOffsetX) {
  return pointX - imageOffsetX
}

export function getPointOriginalOffsetY(pointY, imageOffsetY) {
  return pointY - imageOffsetY
}

export function getPointOriginalOffset(
  projectedOffsetX,
  projectedOffsetY,
  imageOffsetX,
  imageOffsetY
): Point {
  return [
    getPointOriginalOffsetX(projectedOffsetX, imageOffsetX),
    getPointOriginalOffsetY(projectedOffsetY, imageOffsetY),
  ]
}

export const convertCoordinatesDOMtoSVG = (svg, container, x, y) => {
  const pt = svg.createSVGPoint()

  pt.x = x
  pt.y = y
  return pt.matrixTransform(container.getScreenCTM().inverse())
}

export const createCameraViewPath = (distance, rotation, angleWidth) => {
  return {
    innerRadius: 0,
    outerRadius: distance,
    startAngle: geometric.angleToRadians(rotation - angleWidth * 0.5),
    endAngle: geometric.angleToRadians(rotation + angleWidth * 0.5),
    translate: [0, 0],
  }
}

export const createProjectedPositions = (points, offsetX, offsetY) => {
  const cameraPoints = points.map((point) => {
    const { x, y } = point
    const [projectedX, projectedY] = getPointProjectedOffset(
      x,
      y,
      offsetX,
      offsetY
    )
    return { ...point, x: projectedX, y: projectedY }
  })
  return cameraPoints
}

export const translateToRealCoordinates = (points, offsetX, offsetY) => {
  return points.map((point) => {
    const { x, y } = point
    const [realX, realY] = getPointOriginalOffset(x, y, offsetX, offsetY)
    return { ...point, x: realX, y: realY }
  })
}

export const isPointWithinBounds = (x, y, minX, minY, maxX, maxY) => {
  if (x < minX || y < minY || x > maxX || y > maxY) {
    return false
  }
  return true
}

export const isDeviceInCameraRange = (
  deviceX,
  deviceY,
  cameraX,
  cameraY,
  cameraAngle,
  cameraRotation,
  cameraDistance
) => {
  const line: Line = [
    [cameraX, cameraY],
    [deviceX, deviceY],
  ]

  const isDistanceCorrect = cameraDistance > geometric.lineLength(line)

  if (!isDistanceCorrect) return false

  // added 90 to fix the default 0 of the lineAngle method
  const deviceAngle = translateAngleTo360Range(geometric.lineAngle(line) + 90)

  const distance1 = subtractFromBigger(deviceAngle, cameraRotation)
  const distance2 = subtractFromSmaller(deviceAngle, cameraRotation) + 360

  const difference = Math.min(distance1, distance2)

  // if difference between a camera rotation angle and a device angle is smaller
  // than a half of camera viewing angle, then the device is in the camera range

  return difference < cameraAngle * 0.5
}

const getNodeClientRect = (clientNode) => {
  if (clientNode) {
    const { left, top, width, height } = (
      clientNode as HTMLElement
    ).getBoundingClientRect()
    return { x: left, y: top, width, height }
  }
  return { x: 0, y: 0, width: 0, height: 0 }
}

const getDeviceNode = (id: string) => {
  return d3.select(`#device-${id}`).select('.cameraCircle').node()
}

const getTailgateNode = (id: string) => {
  return d3.select(`#connection-${id}`).select('.connectionCircle').node()
}

export const getTailgateClientRect = (id: string): RectI => {
  const clientNode = getTailgateNode(id)
  return getNodeClientRect(clientNode)
}

export const getDeviceClientRect = (id: string): RectI => {
  const clientNode = getDeviceNode(id)
  return getNodeClientRect(clientNode)
}

export const createLineBetweenPoints = (point1: Point, point2: Point): Line => {
  return [point1, point2]
}

// Checkpoint
export const getEditCheckPointIcon = (d: DeviceI) => {
  return d.editCheckpointPos ? 'checkmark.png' : 'edit.png'
}

export const getEditCheckPointGlyph = (d: DeviceI) => {
  return d.editCheckpointPos
    ? FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.endMoving
    : FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.move
}

export const getEditCheckPointColor =
  (colors: FloorPlanVisualColors) => (d: DeviceI) => {
    return d.editCheckpointPos
      ? colors.checkpointEndMovingIcon
      : colors.checkpointMoveIcon
  }

export const getEditCheckPointText = (d: DeviceI) => {
  return d.editCheckpointPos ? 'End Moving' : 'Move Placement'
}

export const getEditCheckPointWidth = (d: DeviceI) => {
  return d.editCheckpointPos ? '130px' : '160px'
}

export const getCheckPointIcon = (d: DeviceI) => {
  return d.isEditMode ? 'checkpoint_selected.png' : 'checkpoint.png'
}

export const getRemoveIconOpacity = (d: DeviceI) => {
  return d.editCheckpointPos
    ? FLOOR_PLAN_CHECKPOINT_ACTION_OPACITY.DISABLED
    : FLOOR_PLAN_CHECKPOINT_ACTION_OPACITY.ACTIVE
}

export const handleCheckPointDeviceDrag = (
  onDeviceDragStart: (d: DeviceI) => void,
  onDeviceUpdated: (d: DeviceI) => void,
  onDeviceReleased: (d: DeviceI) => void
) => {
  return d3
    .drag()
    .on('start', (_event, d: PlacedDeviceI) => {
      if (d.editCheckpointPos) onDeviceDragStart(d)
    })
    .on('drag', (event, d: PlacedDeviceI) => {
      if (d.editCheckpointPos) {
        d.x = event.x
        d.y = event.y
        d.dragged = true
        onDeviceUpdated(d)
      }
    })
    .on('end', (event, d: PlacedDeviceI) => {
      if (d.editCheckpointPos && d.dragged) {
        d.x = event.x
        d.y = event.y
        d.dragged = false
        onDeviceReleased(d)
      }
    })
}
//

export const getTailgateConnectionElementId = (con: ConnectionI) => {
  return con?.deviceId + con?.cameraId
}
